import React, { useRef, useState } from 'react';
import { Form, Input, Select, Typography, Tag as CustomTag } from 'antd';
import styled from 'styled-components';
import { SearchOutlined } from '@ant-design/icons';

// components, keys, and utils
import { Editor as MarkdownEditor } from '../../shared/tabs/Documentation/components/editor/Editor';
import DomainNavigator from '../../../domain/nestedDomains/domainNavigator/DomainNavigator';
import { useEnterKeyListener } from '../../../shared/useEnterKeyListener';
import { BrowserWrapper } from '../../../shared/tags/AddTagsTermsModal';
import { TagTermLabel } from '../../../shared/tags/TagTermLabel';
import { useTranslation } from '../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { DomainLabel } from '../../../shared/DomainLabel';
import ClickOutside from '../../../shared/ClickOutside';
import { DataProductBuilderFormProps } from './types';
import { ANTD_GRAY } from '../../shared/constants';

// generated
import { useGetSearchResultsLazyQuery } from '../../../../graphql/search.generated';
import { Domain, Entity, EntityType } from '../../../../types.generated';

const StyledEditor = styled(MarkdownEditor)`
    border: 1px solid ${ANTD_GRAY[4]};
`;

const CustomChip = styled(CustomTag)`
    > div {
        &:before {
            display: none;
        }
    }
`;

export default function DataProductBuilderForm({
    builderState,
    updateBuilderState,
    showDomain = false,
}: DataProductBuilderFormProps) {
    const { t } = useTranslation();
    function updateName(name: string) {
        // Remove leading spaces to prevent them from being considered as valid input
        const trimmedName = name.trimStart();
        updateBuilderState({
            ...builderState,
            name: trimmedName,
        });
    }

    function updateDescription(description: string) {
        updateBuilderState({
            ...builderState,
            description,
        });
    }

    function updateDomain(domain?: Domain) {
        updateBuilderState({
            ...builderState,
            domain: domain ?? undefined,
        });
    }

    const entityRegistry = useEntityRegistry();
    const [isFocusedOnInput, setIsFocusedOnInput] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [domainSearch, { data: domainSearchData }] = useGetSearchResultsLazyQuery();
    const domainSearchResults =
        domainSearchData?.search?.searchResults?.map((searchResult) => searchResult.entity) || [];
    const inputEl = useRef(null);
    const isShowingDomainNavigator = !inputValue && isFocusedOnInput;

    const handleSearch = (text: string) => {
        domainSearch({
            variables: {
                input: {
                    type: EntityType.Domain,
                    query: text,
                    start: 0,
                    count: 5,
                },
            },
        });
    };

    // Renders a search result in the select dropdown.
    const renderSearchResult = (entity: Entity) => {
        const displayName = entityRegistry.getDisplayName(entity.type, entity);
        return (
            <Select.Option value={entity.urn} key={entity.urn}>
                <DomainLabel name={displayName} />
            </Select.Option>
        );
    };

    const domainResult = !inputValue || inputValue.length === 0 ? [] : domainSearchResults;

    const domainSearchOptions = domainResult?.map((result) => {
        return renderSearchResult(result);
    });

    const onSelectDomain = (newUrn: string) => {
        if (inputEl && inputEl.current) {
            (inputEl.current as any).blur();
        }
        const filteredDomains = domainResult?.filter((entity) => entity.urn === newUrn).map((entity) => entity) || [];
        if (filteredDomains.length) {
            updateDomain(filteredDomains[0] as Domain);
        }
    };

    /**
     * Handles the selection of a domain from the domain navigator.
     *
     * @param domain - The selected domain entity.
     * @remarks
     * - Sets the focus state to false to close the domain navigator.
     * - Updates the builder state with the selected domain entity.
     */
    function selectDomainFromBrowser(domain: Domain) {
        setIsFocusedOnInput(false);
        updateDomain(domain);
    }

    /**
     * Handles the deselection of a domain.
     *
     * @remarks
     * - Clears the input value to reset the search field.
     * - Updates the builder state to remove the selected domain.
     */
    const onDeselectDomain = () => {
        setIsFocusedOnInput(false);
        setInputValue('');
        updateDomain(undefined);
    };

    useEnterKeyListener({
        querySelectorToExecuteClick: '#setDomainButton',
    });

    function handleBlur() {
        setInputValue('');
    }

    function handleCLickOutside() {
        // delay closing the domain navigator so we don't get a UI "flash" between showing search results and navigator
        setTimeout(() => setIsFocusedOnInput(false), 0);
    }

    /**
     * Renders a custom chip component for a selected domain item.
     *
     * @param selectedItem - The currently selected domain entity to display in the chip.
     * @param onClose - A callback function triggered when the chip's close button is clicked.
     * @returns A JSX element representing the custom chip with the selected domain entity.
     *
     * @remarks
     * - The chip is styled with a custom class and includes a close button.
     * - Prevents default mouse down behavior to avoid unintended interactions.
     * */
    const chipRender = ({ selectedItem, onClose }) => {
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };

        return (
            <CustomChip onMouseDown={onPreventMouseDown} closable onClose={onClose} className="f-outlined base-tag">
                <TagTermLabel entity={selectedItem} />
            </CustomChip>
        );
    };

    return (
        <Form layout="vertical" className="mb-0">
            <Form.Item
                required
                hasFeedback
                rules={[{ min: 1, max: 500 }]}
                label={
                    <Typography.Text>
                        <span>{t('Name')}</span>
                        <span className="f-color-risk-very-high f-color-high-contrast">*</span>
                    </Typography.Text>
                }
            >
                <Input
                    autoFocus
                    showCount
                    size="middle"
                    maxLength={100}
                    value={builderState.name}
                    className="f-text-field-primary"
                    placeholder={t('Revenue Dashboards')}
                    onChange={(e) => updateName(e.target.value)}
                />
            </Form.Item>
            <Form.Item label={<Typography.Text>{t('Description')}</Typography.Text>}>
                <StyledEditor doNotFocus content={builderState.description} onChange={updateDescription} />
            </Form.Item>
            {showDomain && (
                <Form.Item
                    required
                    label={
                        <Typography.Text>
                            <span>{t('Domains')}</span>
                            <span className="f-color-risk-very-high f-color-high-contrast">*</span>
                        </Typography.Text>
                    }
                >
                    <ClickOutside onClickOutside={handleCLickOutside}>
                        <Select
                            autoFocus
                            showArrow
                            showSearch
                            value={[]}
                            size="large"
                            mode="multiple"
                            filterOption={false}
                            autoClearSearchValue
                            searchValue={inputValue}
                            aria-label={t('Select Domain')}
                            defaultActiveFirstOption={false}
                            placeholder={
                                <>
                                    <SearchOutlined className="f-color-dark-black-s100" /> {t('Search for Domains')}
                                </>
                            }
                            onSelect={(domainUrn: string) => {
                                onSelectDomain(domainUrn);
                                setInputValue('');
                            }}
                            onDeselect={onDeselectDomain}
                            onSearch={(value: string) => {
                                // eslint-disable-next-line react/prop-types
                                handleSearch(value.trim());
                                // eslint-disable-next-line react/prop-types
                                setInputValue(value);
                            }}
                            ref={inputEl}
                            onBlur={handleBlur}
                            onFocus={() => setIsFocusedOnInput(true)}
                            dropdownStyle={isShowingDomainNavigator ? { display: 'none' } : {}}
                        >
                            {domainSearchOptions}
                        </Select>
                        <BrowserWrapper width="100%" className="f-browser-wrapper" isHidden={!isShowingDomainNavigator}>
                            <DomainNavigator selectDomainOverride={selectDomainFromBrowser} showIcon />
                        </BrowserWrapper>
                        <div className="f-tag-container mt-2">
                            {builderState.domain &&
                                chipRender({
                                    selectedItem: builderState.domain,
                                    onClose: () => {
                                        onDeselectDomain();
                                    },
                                })}
                        </div>
                    </ClickOutside>
                </Form.Item>
            )}
        </Form>
    );
}
